import { AlertCircle, Camera, CheckCircle2, Eye, FileImage, Wifi, WifiOff, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { type ProcessedImage, processImageForUpload, uploadImageToR2 } from "@/lib/imageUtils";
import { saveOfflineImage } from "@/lib/offline/image-storage";

export interface PhotoData {
  url: string;
  caption?: string;
}

interface PhotoUploadProps {
  componentId?: number;
  fieldName: string;
  value?: string | string[] | PhotoData | PhotoData[];
  onChange: (value: string | string[] | PhotoData | PhotoData[]) => void;
  maxFiles?: number;
  allowMultiple?: boolean;
  className?: string;
  projectId?: string;
}

interface UploadedPhoto {
  id?: string;
  url: string;
  caption?: string;
  fileName: string;
  size: number;
  uploadedAt: string;
  key?: string; // R2 storage key
}

interface UploadProgress {
  fileName: string;
  status: "processing" | "uploading" | "complete" | "error";
  progress: number;
  error?: string;
  compressionSavings?: number;
}

const PhotoUpload = ({
  componentId,
  fieldName,
  value,
  onChange,
  maxFiles = 40, // Increased to 40 images
  allowMultiple = true,
  className = "",
  projectId,
}: PhotoUploadProps) => {
  const { toast } = useToast();
  const { isOnline } = useOnlineStatus();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<UploadedPhoto[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<UploadedPhoto | null>(null);
  const baseId = useId();
  const sanitizedFieldName = fieldName.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
  const inputId = `${sanitizedFieldName}-${componentId ?? "component"}-${baseId}`;
  const captionIdPrefix = `${sanitizedFieldName}-caption-${componentId ?? "component"}-${baseId}`;
  const friendlyFieldLabel = fieldName.replace(/[-_]+/g, " ");

  const isUploading = Object.values(uploadProgress).some(
    (p) => p.status === "processing" || p.status === "uploading"
  );

  // Load existing photos from value (URLs or PhotoData objects)
  useEffect(() => {
    if (value) {
      const photos = Array.isArray(value) ? value : [value];

      // Normalize input to PhotoData format
      const incomingPhotos: PhotoData[] = photos
        .filter((item) => item)
        .map((item) => {
          if (typeof item === "string") {
            return { url: item };
          }
          return item as PhotoData;
        });

      // Only update if data has actually changed (avoid resetting on caption changes)
      const currentData = uploadedPhotos
        .map((p) => `${p.url}:${p.caption || ""}`)
        .sort()
        .join("|");
      const newData = incomingPhotos
        .map((p) => `${p.url}:${p.caption || ""}`)
        .sort()
        .join("|");

      if (currentData !== newData) {
        const parsedPhotos: UploadedPhoto[] = incomingPhotos.map((photoData) => {
          // Check if we already have this photo (preserve other data)
          const existingPhoto = uploadedPhotos.find((p) => p.url === photoData.url);
          if (existingPhoto) {
            return { ...existingPhoto, caption: photoData.caption };
          }

          // Extract filename from URL and decode it
          const encodedFileName = photoData.url.split("/").pop() || "Unknown";
          const decodedFileName = decodeURIComponent(encodedFileName);

          // Clean up the filename - remove timestamp and random suffix if present
          // Example: "cropped-image_1761570614866_oqdocef.jpeg" → "cropped-image.jpeg"
          const cleanFileName = decodedFileName.replace(/_\d+_[a-z0-9]+(\.[^.]+)$/, "$1");

          return {
            url: photoData.url,
            caption: photoData.caption,
            fileName: cleanFileName,
            size: 0,
            uploadedAt: new Date().toISOString(),
            key: photoData.url.split("/").slice(3).join("/"), // Extract R2 key from URL
          };
        });
        setUploadedPhotos(parsedPhotos);
      }
    } else if (uploadedPhotos.length > 0) {
      setUploadedPhotos([]);
    }
  }, [value, uploadedPhotos.map, uploadedPhotos.find, uploadedPhotos.length]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    // Check file limits
    const totalFiles = uploadedPhotos.length + files.length;
    if (totalFiles > maxFiles) {
      toast({
        title: "Too Many Files",
        description: `Maximum ${maxFiles} photos allowed. You can upload ${maxFiles - uploadedPhotos.length} more.`,
        variant: "destructive",
      });
      return;
    }

    // Process and upload all selected files
    Array.from(files).forEach((file) => {
      processAndUploadFile(file);
    });

    // Reset input
    event.target.value = "";
  };

  const processAndUploadFile = async (file: File) => {
    const progressId = `${file.name}-${Date.now()}`;

    // Initialize progress tracking
    setUploadProgress((prev) => ({
      ...prev,
      [progressId]: {
        fileName: file.name,
        status: "processing",
        progress: 0,
      },
    }));

    try {
      const processed: ProcessedImage = await processImageForUpload(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        quality: 0.85,
      });

      setUploadProgress((prev) => ({
        ...prev,
        [progressId]: {
          ...prev[progressId],
          status: "uploading",
          progress: 50,
          compressionSavings: processed.compressionRatio,
        },
      }));

      // Step 2: Check if online - if offline, save blob to IndexedDB
      if (!isOnline) {
        if (!projectId) {
          throw new Error("Project ID is required for offline image storage");
        }

        // Save blob to IndexedDB with quote association
        const result = await saveOfflineImage({
          quote_id: `offline_project_${projectId}`, // Temporary quote ID for offline images
          field_name: fieldName,
          component_id: componentId,
          blob: processed.file,
          file_name: file.name,
        });

        if (!result.success) {
          throw new Error(result.error || "Failed to save offline image");
        }

        setUploadProgress((prev) => ({
          ...prev,
          [progressId]: {
            ...prev[progressId],
            status: "complete",
            progress: 100,
          },
        }));

        // Step 3: Add to uploaded photos with offline placeholder URL
        const newPhoto: UploadedPhoto = {
          id: result.image?.id || `offline_${Date.now()}_${Math.random()}`,
          url: `offline://${result.image?.id}`, // Placeholder URL for offline images
          fileName: file.name,
          size: processed.compressedSize,
          uploadedAt: new Date().toISOString(),
        };

        const updatedPhotos = [...uploadedPhotos, newPhoto];
        setUploadedPhotos(updatedPhotos);
        updateValue(updatedPhotos);

        toast({
          title: "Photo Saved Offline",
          description: `${file.name} will be uploaded when back online (${processed.compressionRatio.toFixed(0)}% compression)`,
        });

        // Clean up progress after 2 seconds
        setTimeout(() => {
          setUploadProgress((prev) => {
            const next = { ...prev };
            delete next[progressId];
            return next;
          });
        }, 2000);
        return;
      }

      if (!projectId) {
        throw new Error("Project ID is required for image uploads");
      }

      const uploadResult = await uploadImageToR2(processed.file, projectId);

      setUploadProgress((prev) => ({
        ...prev,
        [progressId]: {
          ...prev[progressId],
          status: "complete",
          progress: 100,
        },
      }));

      // Step 3: Add to uploaded photos
      const newPhoto: UploadedPhoto = {
        id: `photo_${componentId ?? "global"}_${sanitizedFieldName}_${Date.now()}_${Math.random()}`,
        url: uploadResult.url,
        key: uploadResult.key,
        fileName: file.name,
        size: processed.compressedSize,
        uploadedAt: new Date().toISOString(),
      };

      const updatedPhotos = [...uploadedPhotos, newPhoto];
      setUploadedPhotos(updatedPhotos);
      updateValue(updatedPhotos);

      toast({
        title: "Photo Uploaded",
        description: `${file.name} uploaded successfully (${processed.compressionRatio.toFixed(0)}% compression)`,
      });

      // Clean up progress after 2 seconds
      setTimeout(() => {
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[progressId];
          return next;
        });
      }, 2000);
    } catch (error) {
      setUploadProgress((prev) => ({
        ...prev,
        [progressId]: {
          ...prev[progressId],
          status: "error",
          progress: 0,
          error: error instanceof Error ? error.message : "Upload failed",
        },
      }));

      toast({
        title: "Upload Failed",
        description: `Failed to upload ${file.name}: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });

      // Clean up error after 5 seconds
      setTimeout(() => {
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[progressId];
          return next;
        });
      }, 5000);
    }
  };

  const removePhoto = (photoIndex: number) => {
    const _photo = uploadedPhotos[photoIndex];

    // TODO: Optionally delete from R2 via API
    // For now, just remove from UI - images remain in R2
    // Could call: DELETE /api/v1/images/{key}

    const updatedPhotos = uploadedPhotos.filter((_, index) => index !== photoIndex);
    setUploadedPhotos(updatedPhotos);
    updateValue(updatedPhotos);

    toast({
      title: "Photo Removed",
      description: "Photo has been removed from the gallery",
    });
  };

  const updatePhotoCaption = (photoIndex: number, caption: string) => {
    const updatedPhotos = [...uploadedPhotos];
    updatedPhotos[photoIndex] = { ...updatedPhotos[photoIndex], caption };
    setUploadedPhotos(updatedPhotos);
    updateValue(updatedPhotos);
  };

  const updateValue = (photos: UploadedPhoto[]) => {
    if (allowMultiple) {
      // Return array of PhotoData objects with url and caption
      onChange(photos.map((photo) => ({ url: photo.url, caption: photo.caption })));
    } else {
      // Return single PhotoData object or empty string
      onChange(photos.length > 0 ? { url: photos[0].url, caption: photos[0].caption } : "");
    }
  };

  const handlePreview = (photo: UploadedPhoto) => {
    setPreviewPhoto(photo);
    setShowPreview(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
      return "0 B";
    }
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          accept="image/*,.heic,.heif"
          multiple={allowMultiple}
          onChange={handleFileSelect}
          className="hidden"
          aria-label={`Upload ${friendlyFieldLabel} photos`}
        />

        <div className="space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
            <Camera className="w-6 h-6 text-gray-400" />
          </div>

          <div>
            <h3 className="font-medium text-gray-900">Upload Photos</h3>
            <p className="text-sm text-gray-500">Click to select or drag and drop images</p>
            <p className="text-xs text-gray-400 mt-1">
              Max {maxFiles} files. JPG, PNG, GIF, WebP, HEIC supported.
            </p>
            <p className="text-xs text-green-600 mt-1">
              ✓ HEIC images auto-convert to JPEG • Images compressed to ~1MB
            </p>
            {!isOnline && (
              <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                <WifiOff className="w-3 h-3" />
                Offline: Photos will be saved locally and uploaded when back online
              </p>
            )}
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || uploadedPhotos.length >= maxFiles}
            aria-controls={inputId}
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                Processing...
              </>
            ) : (
              <>
                {isOnline ? (
                  <Wifi className="w-4 h-4 mr-2" />
                ) : (
                  <WifiOff className="w-4 h-4 mr-2 text-orange-500" />
                )}
                Select Photos
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Upload Progress */}
      {Object.entries(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([id, progress]) => (
            <Card key={id} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {progress.status === "complete" ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : progress.status === "error" ? (
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      ) : (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current flex-shrink-0" />
                      )}
                      <span className="truncate font-medium">{progress.fileName}</span>
                    </div>
                    {progress.compressionSavings !== undefined && (
                      <Badge variant="secondary" className="text-xs ml-2">
                        {progress.compressionSavings.toFixed(0)}% saved
                      </Badge>
                    )}
                  </div>
                  {progress.status !== "error" && (
                    <Progress value={progress.progress} className="h-1" />
                  )}
                  {progress.status === "processing" && (
                    <p className="text-xs text-gray-500">Converting and compressing...</p>
                  )}
                  {progress.status === "uploading" && (
                    <p className="text-xs text-gray-500">Uploading to cloud storage...</p>
                  )}
                  {progress.status === "error" && (
                    <p className="text-xs text-red-500">{progress.error}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Uploaded Photos */}
      {uploadedPhotos.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="font-medium">Uploaded Photos ({uploadedPhotos.length})</Label>
            <Badge variant="secondary">
              {uploadedPhotos.length}/{maxFiles}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {uploadedPhotos.map((photo, index) => {
              const captionId = `${captionIdPrefix}-${index}`;

              return (
                <Card key={photo.id || index} className="overflow-hidden">
                  <CardContent className="p-3">
                    <div className="flex gap-3">
                      {/* Thumbnail */}
                      <div className="relative flex-shrink-0">
                        <button
                          type="button"
                          className="block"
                          onClick={() => handlePreview(photo)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handlePreview(photo);
                            }
                          }}
                          aria-label={`Preview ${photo.fileName}`}
                        >
                          <img
                            src={photo.url}
                            alt={photo.fileName}
                            className="w-16 h-16 object-contain rounded bg-gray-50"
                          />
                        </button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                          onClick={() => removePhoto(index)}
                          aria-label={`Remove ${photo.fileName}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>

                      {/* Photo Info */}
                      <div className="flex-1 min-w-0 space-y-2">
                        <div>
                          <p className="font-medium text-sm truncate">{photo.fileName}</p>
                          <p className="text-xs text-gray-500">
                            {photo.size > 0 ? formatFileSize(photo.size) : "Uploaded"} •{" "}
                            {new Date(photo.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>

                        <div className="space-y-1">
                          <Label htmlFor={captionId} className="text-xs">
                            Caption
                          </Label>
                          <Input
                            id={captionId}
                            placeholder="Add a caption..."
                            value={photo.caption || ""}
                            onChange={(e) => updatePhotoCaption(index, e.target.value)}
                            className="h-7 text-xs"
                          />
                        </div>

                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePreview(photo)}
                            className="h-6 text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileImage className="w-5 h-5" />
              {previewPhoto?.fileName}
            </DialogTitle>
            <DialogDescription>
              {previewPhoto && (
                <span>
                  {previewPhoto.size > 0 ? formatFileSize(previewPhoto.size) : "Cloud storage"} •
                  Uploaded {new Date(previewPhoto.uploadedAt).toLocaleString()}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {previewPhoto && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img
                  src={previewPhoto.url}
                  alt={previewPhoto.fileName}
                  className="max-w-full max-h-[60vh] object-contain rounded"
                />
              </div>

              {previewPhoto.caption && (
                <div className="text-center">
                  <p className="text-sm text-gray-600 italic">"{previewPhoto.caption}"</p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoUpload;
